import { useState, useEffect, useCallback } from 'react';
import {
  IUserSettings,
  IPrivacySettings,
  INotificationPreferences
} from '../../types/profile.types';
import userSettingsService from '../../services/api/userSettingsService';

interface UseUserSettingsResult {
  settings: IUserSettings | null;
  loading: boolean;
  error: string | null;
  updatePrivacy: (data: Partial<IPrivacySettings>) => Promise<void>;
  updateNotifications: (
    data: Partial<INotificationPreferences>
  ) => Promise<void>;
}

export function useUserSettings(): UseUserSettingsResult {
  const [settings, setSettings] = useState<IUserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    userSettingsService
      .getSettings()
      .then((data) => {
        if (!cancelled) {
          setSettings(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load settings');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const updatePrivacy = useCallback(
    async (data: Partial<IPrivacySettings>): Promise<void> => {
      const updated = await userSettingsService.updatePrivacySettings(data);
      setSettings((prev) => (prev ? { ...prev, privacy: updated } : null));
    },
    []
  );

  const updateNotifications = useCallback(
    async (data: Partial<INotificationPreferences>): Promise<void> => {
      const updated =
        await userSettingsService.updateNotificationPreferences(data);
      setSettings((prev) =>
        prev ? { ...prev, notifications: updated } : null
      );
    },
    []
  );

  return { settings, loading, error, updatePrivacy, updateNotifications };
}
